/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, {useState} from 'react'
import {Button, Spinner} from '@chakra-ui/react'
import FileInfo from '@/pages/Assets/pages/UploadRegisterAsset/components/UploadFile/components/FileInfo'
import DragAndDropFile from '@/pages/Assets/pages/UploadRegisterAsset/components/UploadFile/components/DragAndDropFile'
import {ASSET_FILE_TOO_LARGE} from '@/configs/strings/assets'
import {assetLimitFileSize} from '@/configs/env-vars'

const UploadFile = ({onFileSelected, isSubmitting = false, setErrorMessage}) => {
    const [file, setFile] = useState(null)
    const [fileName, setFileName] = useState('')
    const [fileSize, setFileSize] = useState('')

    const onDeleteFile = () => {
        setFileName('')
        setFileSize('')
        setFile(null)
        onFileSelected(null)
    }

    const onDrop = (acceptedFiles) => {
        setErrorMessage && setErrorMessage(null, true)

        const isLarge = acceptedFiles.find(file => file.size > assetLimitFileSize)

        if (isLarge) {
            setErrorMessage && setErrorMessage(ASSET_FILE_TOO_LARGE)
            setFile(null)
            setFileName(null)
            setFileSize(null)
            onFileSelected(null)

            return null
        }

        if (acceptedFiles.length > 0) {
            const selectedFile = acceptedFiles[0]
            setFile(selectedFile)
            setFileName(selectedFile.name)
            setFileSize(selectedFile.size)
            onFileSelected(selectedFile)
        }
    }

    return (
        <>
            {isSubmitting
                ? (
                    <div className="text-center">
                        <Spinner size='xl'/>
                    </div>
                )
                : (
                    <div className="bg-gray-200 p-4 rounded-lg">
                        <DragAndDropFile
                            onDrop={onDrop}
                            accept={{
                                'application/epub+zip': ['.epub'],
                                'application/pdf': ['.pdf'],
                                'text/plain': ['.txt'],
                                'application/xml': ['.xml'],
                                'audio/mpeg': ['.mp3'],
                                'audio/wav': ['.wav'],
                                'audio/x-m4a': ['.m4a'],
                                'audio/mp4': ['.m4a'],
                                'image/jpeg': ['.jpg', '.jpeg'],
                                'image/png': ['.png'],
                                'application/vnd.las': ['.las'],
                                'application/octet-stream': ['.laz', '.ply', '.rcs', '.rcp', '.ptg', '.warc', '.arc'],
                                'application/e57': ['.e57'],
                                'image/x-photo-cd': ['.pcd'],
                                'application/warc': ['.warc'],
                                'text/html': ['.html'],
                                'video/quicktime': ['.mov'],
                                'video/vnd.avi': ['.avi'],
                                'video/x-msvideo': ['.avi'],
                                'image/heic': ['.heic'],
                                'image/heif': ['.heif'],
                                'image/svg+xml': ['.svg'],
                                'text/csv': ['.csv'],
                                'text/markdown': ['.md'],
                            }}
                            multiple={false}
                        />
                        {fileName && fileSize &&
                            <FileInfo
                                onDeleteFile={onDeleteFile}
                                fileName={fileName}
                                fileSize={fileSize}
                            />
                        }

                        {file && (
                            <Button
                                type="submit"
                                className="mt-4 w-full"
                                isDisabled={!file || isSubmitting}
                            >
                                Continue with Selected File
                            </Button>
                        )}
                    </div>
                )}
        </>
    )
}

export default UploadFile
